import { useInfiniteQuery, InfiniteData } from "@tanstack/react-query";
import { FullTask } from "../types/FullTask";
import { ActivityFilterType } from "../types/filterOptionsTypes";
import { TasksTabs } from "../constants/tasksTabs";
import { httpClient } from "@coworker/http-client";
import { Task } from "../../task-management/types/Task";
import { useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  CLOSED_TASKS_QUERY_KEY,
  COMPLETED_TASKS_QUERY_KEY,
  IN_PROGRESS_TASKS_QUERY_KEY,
  OPEN_TASKS_QUERY_KEY,
  OVERVIEW_TASKS_QUERY_KEY,
  UPCOMING_TASKS_QUERY_KEY,
} from "../constants/queryKeys";
import { getTasksEndpointUrl } from "../helpers/getTasksEndpointUrl";
import { addTeamFiltersToQueryOptions } from "../helpers/addTeamFiltersToQueryOptions";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";

// These are only the properties used for filtering, not ordering
export type StrictFilters = Partial<
  Pick<
    ActivityFilterType,
    | "taskType"
    | "createdBy"
    | "assignedTo"
    | "locations"
    | "pickupLocations"
    | "refillLocations"
    | "priority"
    | "sortedBy"
    | "sortedByStart"
    | "quantityType"
    | "includeUpcoming"
    | "period"
    | "startDate"
    | "endDate"
  >
>;

// Used by the v2 keyset path
type CursorPageParam = {
  lastSeenTaskCreatedAt: string | null;
  lastSeenTaskId: string | null;
} | null;

type V2PageParam = CursorPageParam | number;

export type TaskListItem =
  | { type: "v1"; task_data: FullTask; locationCode?: string }
  | { type: "v2"; task_data: Task; locationCode?: string };

type TaskQueryResult = {
  list: TaskListItem[];
  totalCount: number;
  filteredCount: number;
  // v2 keyset fields — present when ENABLE_KEY_SET_GET_TASKS is on
  lastSeenTaskCreatedAt: string | null;
  lastSeenTaskId: string | null;
  // v1 offset field — present when ENABLE_KEY_SET_GET_TASKS is off
  page?: number;
  locationGroups?: Array<{ locationCode: string; taskCount: number }>;
};

const tasksOverviewTabMap: Record<
  TasksTabs,
  { state: string; queryKey: string }
> = {
  [TasksTabs.OPEN]: { state: "open", queryKey: OPEN_TASKS_QUERY_KEY },
  [TasksTabs.IN_PROGRESS]: {
    state: "in_progress",
    queryKey: IN_PROGRESS_TASKS_QUERY_KEY,
  },
  [TasksTabs.UPCOMING]: {
    state: "pending",
    queryKey: UPCOMING_TASKS_QUERY_KEY,
  },
  [TasksTabs.COMPLETED]: {
    state: "completed",
    queryKey: COMPLETED_TASKS_QUERY_KEY,
  },
  [TasksTabs.CLOSED]: { state: "closed", queryKey: CLOSED_TASKS_QUERY_KEY },
};

export const useTasksOverview = (
  filters: StrictFilters,
  activeTab: TasksTabs,
  enabled = true
) => {
  const uid = useUID();
  const isKeysetEnabled = useFlag(FLAGS.ENABLE_KEY_SET_GET_TASKS);
  const { state, queryKey } = tasksOverviewTabMap[activeTab];

  const getSupportedSortedBy = () => {
    let sortedBy = filters.sortedBy!;

    if (!isKeysetEnabled && sortedBy === "smart") {
      sortedBy = "createdAtDesc";
    }

    if (state === "completed" || state === "closed") {
      if (sortedBy === "createdAtDesc") {
        return "finishedAtDesc";
      }
      if (sortedBy === "createdAtAsc") {
        return "finishedAtAsc";
      }
    }

    return sortedBy;
  };

  // sorted array of filter values to key the query along with keys
  // it is sorted so the query caching doesn't create multiple queries for the same
  // filter values but in different order
  const filtersIndex = [];
  for (const key in filters) {
    if (filters[key as keyof StrictFilters]) {
      filtersIndex.push(`${key}=${filters[key as keyof StrictFilters]}`);
    }
  }
  filtersIndex.sort();

  // ---------------------------------------------------------------------------
  // v2 keyset path (ENABLE_KEY_SET_GET_TASKS = true)
  // ---------------------------------------------------------------------------

  const getTasksQueryFunctionV2 = async (
    state: string,
    { pageParam }: { pageParam: V2PageParam }
  ) => {
    const sortedBy = getSupportedSortedBy();
    const requestSortedBy =
      activeTab === TasksTabs.UPCOMING
        ? filters.sortedByStart || "startAsc"
        : sortedBy;
    const isSmartSort = requestSortedBy === "smart";
    const page = typeof pageParam === "number" ? pageParam : 1;

    const queryOptions = {
      state,
      ...filters,
      pageSize: "20",
      sortedBy: requestSortedBy,
      includeUpcoming: activeTab === TasksTabs.UPCOMING ? true : false,
      page: isSmartSort ? page : undefined,
      lastSeenTaskCreatedAt:
        !isSmartSort && typeof pageParam !== "number"
          ? (pageParam?.lastSeenTaskCreatedAt ?? undefined)
          : undefined,
      lastSeenTaskId:
        !isSmartSort && typeof pageParam !== "number"
          ? (pageParam?.lastSeenTaskId ?? undefined)
          : undefined,
    };

    await addTeamFiltersToQueryOptions(
      uid,
      queryOptions,
      filters.createdBy || "",
      filters.assignedTo || ""
    );

    const url = `/v2/${getTasksEndpointUrl(queryOptions)}`;
    const result = await httpClient.tasks.get<{ data: TaskQueryResult }>(url);

    return isSmartSort ? { ...result.data, page } : result.data;
  };

  const getNextPageParamV2 = (
    lastPage: TaskQueryResult,
    allPages: TaskQueryResult[]
  ): V2PageParam | undefined => {
    if (
      getSupportedSortedBy() === "smart" &&
      activeTab !== TasksTabs.UPCOMING
    ) {
      const loaded = allPages.reduce(
        (sum, p) => sum + (p.list?.length ?? 0),
        0
      );
      if (!lastPage.list?.length || loaded >= (lastPage.filteredCount ?? 0)) {
        return undefined;
      }

      return (lastPage.page ?? 1) + 1;
    }

    if (
      lastPage.lastSeenTaskCreatedAt !== null &&
      lastPage.lastSeenTaskId !== null
    ) {
      return {
        lastSeenTaskCreatedAt: lastPage.lastSeenTaskCreatedAt,
        lastSeenTaskId: lastPage.lastSeenTaskId,
      };
    }
    return undefined;
  };

  // ---------------------------------------------------------------------------
  // v1 offset path (ENABLE_KEY_SET_GET_TASKS = false)
  // ---------------------------------------------------------------------------

  const getTasksQueryFunctionV1 = async (
    state: string,
    { pageParam = 1 }: { pageParam: string | number }
  ) => {
    const sortedBy = getSupportedSortedBy();

    const queryOptions = {
      state,
      ...filters,
      page: pageParam as number,
      pageAfter: pageParam as string,
      sortedBy:
        activeTab === TasksTabs.UPCOMING
          ? filters.sortedByStart || "startAsc"
          : sortedBy,
      includeUpcoming: activeTab === TasksTabs.UPCOMING ? true : false,
    };

    await addTeamFiltersToQueryOptions(
      uid,
      queryOptions,
      filters.createdBy || "",
      filters.assignedTo || ""
    );

    const url = `/v1/${getTasksEndpointUrl(queryOptions)}`;
    const result = await httpClient.tasks.get<{ data: TaskQueryResult }>(url);

    return { ...result.data, page: pageParam } as TaskQueryResult;
  };

  const getNextPageParamV1 = (
    lastPage: TaskQueryResult,
    allPages: TaskQueryResult[]
  ) => {
    const loaded = allPages.reduce((sum, p) => sum + (p.list?.length ?? 0), 0);
    if (!lastPage.list?.length || loaded >= (lastPage.filteredCount ?? 0)) {
      return undefined;
    }

    return (lastPage.page ?? 1) + 1;
  };

  // ---------------------------------------------------------------------------
  // Return the appropriate query based on the flag
  // ---------------------------------------------------------------------------

  const v2Query = useInfiniteQuery<
    TaskQueryResult,
    Error,
    InfiniteData<TaskQueryResult>,
    unknown[],
    V2PageParam
  >({
    queryKey: [OVERVIEW_TASKS_QUERY_KEY, queryKey, "keyset", ...filtersIndex],
    queryFn: async ({ pageParam }) =>
      getTasksQueryFunctionV2(state, {
        pageParam: pageParam as V2PageParam,
      }),
    initialPageParam: null,
    getNextPageParam: getNextPageParamV2,
    staleTime: 0,
    enabled: enabled && isKeysetEnabled,
    refetchOnWindowFocus: true,
  });

  const v1Query = useInfiniteQuery<TaskQueryResult>({
    queryKey: [OVERVIEW_TASKS_QUERY_KEY, queryKey, "offset", ...filtersIndex],
    queryFn: async ({ pageParam }) =>
      getTasksQueryFunctionV1(state, {
        pageParam: pageParam as string | number,
      }),
    initialPageParam: 1,
    getNextPageParam: getNextPageParamV1,
    staleTime: 0,
    enabled: enabled && !isKeysetEnabled,
    refetchOnWindowFocus: true,
  });

  return isKeysetEnabled ? v2Query : v1Query;
};
